/**
 * SBI branding: Fresenius Kabi styling can be toggled via the landing screen button.
 * Value is persisted in sessionStorage so all pages use the same branding after navigation.
 */

import { createContext, useContext, useMemo, useState, useCallback } from "react";
import PropTypes from "prop-types";

// Values that enable Fresenius Kabi branding (stored in sessionStorage)
export const FRESENIUS_BRANDING_VALUES = ["fresenius", "fresenius-kabi"];

// Fresenius Kabi colors and theme (light/dark) – use when isFreseniusBranding
export const FRESENIUS = {
  blue: "#00549F",
  blueLight: "#2070b8",
  blueDark: "#003d75",
  border: "rgba(255, 255, 255, 0.35)",
  borderStrong: "rgba(255, 255, 255, 0.5)",
  white: "#ffffff",
  /** Fresenius dark theme (header + content) – respects light/dark toggle */
  themeDark: {
    bgMain: "#003d75",
    bgHeader: "#003d75",
    border: "rgba(255, 255, 255, 0.5)",
    link: "#ffffff",
    textPrimary: "#ffffff",
  },
  /** Fresenius light theme (header + content) – respects light/dark toggle */
  themeLight: {
    bgMain: "#e8f2fa",
    bgHeader: "#e0f0fa", /* lighter blue for header so logos have more contrast */
    border: "#00549F",
    link: "#003d75",
    textPrimary: "#1a3d40",
  },
};

const STORAGE_KEY = "sbiBranding";

const BrandingContext = createContext(null);
BrandingContext.displayName = "BrandingContext";

function getStoredBranding() {
  try {
    return typeof window !== "undefined" ? window.sessionStorage.getItem(STORAGE_KEY) : null;
  } catch {
    return null;
  }
}

function setStoredBranding(value) {
  try {
    if (typeof window !== "undefined") {
      if (value) window.sessionStorage.setItem(STORAGE_KEY, value);
      else window.sessionStorage.removeItem(STORAGE_KEY);
    }
  } catch {}
}

function getInitialBranding() {
  const stored = getStoredBranding();
  if (!stored) return true; // default: Fresenius
  return FRESENIUS_BRANDING_VALUES.includes(stored.toLowerCase());
}

export function BrandingProvider({ children }) {
  const [isFreseniusBranding, setIsFreseniusBranding] = useState(getInitialBranding);

  const setFreseniusBranding = useCallback((enabled) => {
    setIsFreseniusBranding(enabled);
    setStoredBranding(enabled ? "fresenius" : "sbi");
  }, []);

  const value = useMemo(
    () => ({
      isFreseniusBranding,
      setFreseniusBranding,
      /** Append to path for links when branding is active (keeps URL in sync if needed) */
      appendBrandingSearch: (path) => path,
    }),
    [isFreseniusBranding, setFreseniusBranding]
  );

  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
}

BrandingProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export function useBranding() {
  const ctx = useContext(BrandingContext);
  if (!ctx) {
    return {
      isFreseniusBranding: false,
      setFreseniusBranding: () => {},
      appendBrandingSearch: (path) => path,
    };
  }
  return ctx;
}
